import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { useMedia } from 'react-use';
import '../../../../constants/icons/index.mjs';
import '../../../../constants/icons/header/index.mjs';
import { BREAKPOINTS } from '../../../../constants/layout.mjs';
import { ExternalUrl } from '../../../../constants/url.mjs';
import '../../../../contexts/MenuContext/index.mjs';
import { SiteId } from '../../../../contexts/MenuContext/mapping.mjs';
import { isUrlPathname } from '../../../../contexts/MenuContext/utils.mjs';
import '../../../../contexts/MicrositeMenuContext/index.mjs';
import { SHARED_MENU_ITEMS } from '../../../../contexts/MicrositeMenuContext/mapping.mjs';
import { useMicrositeMenuTranslations } from '../../../../contexts/MicrositeMenuContext/translations.mjs';
import { ModalId } from '../../../../contexts/MicrositeMenuContext/types.mjs';
import '../../../Header/AccountFrame/index.mjs';
import '../../../Header/AccountFrame/Mobile/index.mjs';
import { SkeletonCircleLoader } from '../../../Header/Header.styles.mjs';
import '../../../Header/LanguageFrame/index.mjs';
import '../../../Header/LanguageFrame/Mobile/index.mjs';
import { MenuButton } from '../../../Header/MenuItem/MenuItem.mjs';
import '../../../Header/MicrositeMobileMenu/index.mjs';
import '../../../MobileModal/index.mjs';
import '../../../StyleableSvgImage/index.mjs';
import '../DropdownMenu/index.mjs';
import '../SiteMap/index.mjs';
import './_components/NavigationItem/index.mjs';
import './_components/SubNavigation/index.mjs';
import { StyledContainer, StableGutterGlobalStyles, Wrap, InnerWrap, TransparentButton, LeftWrap, LogoAnchor, ItemsWrap, RightWrap, RightItem, ActionIcon, ContentContainer, Content, ModalMenuWrap, ModalMenuInnerWrap, OverflowWrapper, ModalWrap, ModalInnerWrap } from './HeaderContent.styles.mjs';
import { LIGHT_KEY, HeaderMotif, DROPDOWN_KEY } from './HeaderContent.types.mjs';
import { useMicrositeMenu } from '../../../../contexts/MicrositeMenuContext/context.mjs';
import { useMenu } from '../../../../contexts/MenuContext/context.mjs';
import { SiteMap } from '../SiteMap/SiteMap.mjs';
import { StyleableSvgImage } from '../../../StyleableSvgImage/StyleableSvgImage.mjs';
import _externalAssets_DtGmfNK5__default from "../../../../assets/closeLarge.svg";
import _externalAssets_C79$rFpJ__default from "../../../../assets/hamburgerV2.svg";
import logoUnifi__default from "../../../../assets/logoUnifi.svg";
import { NavigationItem } from './_components/NavigationItem/NavigationItem.mjs';
import { DropdownMenu } from '../DropdownMenu/DropdownMenu.mjs';
import { SubNavigation } from './_components/SubNavigation/SubNavigation.mjs';
import globe__default from "../../../../assets/globe2.svg";
import accountLogo__default from "../../../../assets/account.svg";
import { MicrositeMobileMenu } from '../../../Header/MicrositeMobileMenu/MicrositeMobileMenu.mjs';
import { MobileModal } from '../../../MobileModal/MobileModal.mjs';
import { LanguageFrameMobile } from '../../../Header/LanguageFrame/Mobile/Mobile.mjs';
import { LanguageFrame } from '../../../Header/LanguageFrame/LanguageFrame.mjs';
import { AccountFrameMobile } from '../../../Header/AccountFrame/Mobile/Mobile.mjs';
import { AccountFrame } from '../../../Header/AccountFrame/AccountFrame.mjs';
"use strict";
const HEADER_ICON_SIZE = 20;
const HeaderContent = ({
  menu,
  onModalOpen,
  onLanguageChange,
  onMenuItemClick,
  motif,
  account
}) => {
  const isMobile = useMedia(BREAKPOINTS.TABLET_AND_DOWN, false);
  const wrapperRef = useRef(null);
  const [hasWhiteBackground, setHasWhiteBackground] = useState(motif === "light");
  const [showSiteMap, setShowSiteMap] = useState(false);
  const {
    activeModal,
    setActiveModal
  } = useMicrositeMenu();
  const {
    createSiteUrl
  } = useMenu();
  const {
    language
  } = useMicrositeMenu();
  const {
    formatMessage
  } = useMicrositeMenuTranslations(language);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [currentSelectedMenu, setCurrentSelectedMenu] = useState(null);
  const [isUsingTouchScreen, setIsUsingTouchScreen] = useState(false);
  const hasDarkText = useMemo(() => {
    if (motif === LIGHT_KEY) {
      return true;
    }
    const activeParentElement = menu?.items?.find(({
      active
    }) => !!active);
    if (!activeParentElement) {
      return false;
    }
    const activeChildElement = activeParentElement.items?.find(({
      active
    }) => !!active);
    return activeChildElement?.hasDarkText ?? false;
  }, [menu, motif]);
  const theme = useMemo(() => {
    if (hasDarkText) {
      return HeaderMotif.DarkText;
    }
    return hasWhiteBackground || !!selectedMenu ? HeaderMotif.DarkText : HeaderMotif.LightText;
  }, [hasWhiteBackground, selectedMenu, hasDarkText]);
  useEffect(() => {
    setSelectedMenu(null);
    if (!wrapperRef?.current || motif === "light") return;
    const observer = new IntersectionObserver(([entry]) => setHasWhiteBackground(!entry?.isIntersecting), {
      threshold: 0.5
    });
    observer.observe(wrapperRef.current);
    return () => {
      observer.disconnect();
    };
  }, [motif]);
  useEffect(() => {
    if (motif === "light") {
      setHasWhiteBackground(true);
    }
  }, [motif]);
  const handleModalClose = useCallback(() => {
    setActiveModal(null);
  }, [setActiveModal]);
  const handleModalOpen = useCallback((e, id) => {
    e.stopPropagation();
    if (activeModal !== id) {
      onModalOpen?.();
    }
    setActiveModal(activeModal === id ? null : id);
  }, [activeModal, setActiveModal, onModalOpen]);
  const handleMenuItemClick = (event, url) => {
    onMenuItemClick(event, url);
    if (!selectedMenu) {
      return;
    }
    const items = (selectedMenu?.items ?? []).map(item => ({
      ...item,
      active: item.url ? isUrlPathname(url, item.url) : false
    }));
    setSelectedMenu({
      ...selectedMenu,
      items
    });
  };
  const handleSiteMapClose = () => {
    setSelectedMenu(null);
    setShowSiteMap(false);
  };
  const handleHamburgerClick = useCallback(e => {
    setHasWhiteBackground(true);
    handleModalOpen(e, ModalId.MobileMenu);
  }, [handleModalOpen]);
  useEffect(() => {
    if (!!activeModal || !!selectedMenu) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [activeModal, selectedMenu]);
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    if (activeModal) {
      setVh();
    }
    setVh();
    window.addEventListener("resize", setVh);
    window.addEventListener("orientationchange", setVh);
    return () => {
      window.removeEventListener("resize", setVh);
      window.removeEventListener("orientationchange", setVh);
    };
  }, [activeModal]);
  return /* @__PURE__ */jsxs(StyledContainer, {
    ref: wrapperRef,
    children: [/* @__PURE__ */jsx(StableGutterGlobalStyles, {}), showSiteMap ? /* @__PURE__ */jsx(SiteMap, {
      menu: SHARED_MENU_ITEMS ?? [],
      show: showSiteMap,
      onCloseSitemap: handleSiteMapClose,
      onLinkClick: (e, url) => {
        handleSiteMapClose();
        onMenuItemClick?.(e, url);
      }
    }) : /* @__PURE__ */jsxs(Wrap, {
      $isLastMenu: true,
      $hasWhiteBackground: hasWhiteBackground,
      $transitionFade: !!selectedMenu,
      $isActive: activeModal === ModalId.MobileMenu,
      $shouldAnimateBackground: motif !== "light",
      children: [/* @__PURE__ */jsxs(InnerWrap, {
        $motif: theme,
        children: [/* @__PURE__ */jsx(TransparentButton, {
          $shouldHideOnDesktop: true,
          onClick: e => handleHamburgerClick(e),
          children: /* @__PURE__ */jsx(StyleableSvgImage, {
            src: activeModal === ModalId.MobileMenu ? _externalAssets_DtGmfNK5__default : _externalAssets_C79$rFpJ__default,
            className: "hamburger-icon",
            width: HEADER_ICON_SIZE,
            height: HEADER_ICON_SIZE
          })
        }), /* @__PURE__ */jsxs(LeftWrap, {
          children: [/* @__PURE__ */jsx(LogoAnchor, {
            $motif: theme,
            href: menu?.url ?? "",
            onClick: e => {
              e.preventDefault();
              onMenuItemClick?.(e, menu?.url ?? "");
              if (activeModal === ModalId.MobileMenu) {
                setActiveModal(null);
              }
            },
            children: /* @__PURE__ */jsx(StyleableSvgImage, {
              src: logoUnifi__default,
              width: 46,
              height: 14,
              ...(isMobile && {
                $active: false
              })
            })
          }), /* @__PURE__ */jsxs(ItemsWrap, {
            onMouseLeave: () => setSelectedMenu(null),
            children: [(menu?.items ?? []).map((item, index) => {
              const currentlyActiveMenu = selectedMenu;
              return /* @__PURE__ */jsx(NavigationItem, {
                menuIsClosed: selectedMenu === null,
                item,
                motif: theme,
                isActive: item.id === selectedMenu?.id,
                onMouseEnter: () => setSelectedMenu(item),
                onTouchStart: () => {
                  setIsUsingTouchScreen(true);
                  setCurrentSelectedMenu(currentlyActiveMenu);
                  setSelectedMenu(item);
                },
                onTouchEnd: () => {
                  if (selectedMenu?.id === currentSelectedMenu?.id) {
                    setSelectedMenu(null);
                  }
                },
                onClick: event => {
                  if (item.variant !== "dropdown" && !isUsingTouchScreen) {
                    handleMenuItemClick(event, item.url);
                  }
                }
              }, `${item.id}-${item.label}-${index}`);
            }), /* @__PURE__ */jsx(DropdownMenu, {
              items: selectedMenu?.items ?? [],
              show: !!selectedMenu?.id && selectedMenu?.variant === DROPDOWN_KEY,
              onSiteMapClick: () => setShowSiteMap(true),
              onMenuItemClick,
              onMouseLeave: () => setSelectedMenu(null),
              onClose: () => setSelectedMenu(null)
            }), /* @__PURE__ */jsx(SubNavigation, {
              menu: selectedMenu,
              show: !!selectedMenu?.id && selectedMenu?.variant !== DROPDOWN_KEY,
              onMenuItemClick: handleMenuItemClick
            })]
          })]
        }), /* @__PURE__ */jsxs(RightWrap, {
          children: [/* @__PURE__ */jsx(RightItem, {
            href: createSiteUrl([SiteId.Ui, "/whats-new"]),
            $motif: theme,
            $withMarginRight: true,
            children: formatMessage("commons.whatsNew")
          }), /* @__PURE__ */jsx(RightItem, {
            href: ExternalUrl.SUPPORT,
            $motif: theme,
            children: formatMessage("commons.support")
          }), /* @__PURE__ */jsx(MenuButton, {
            href: createSiteUrl([SiteId.Store, "/"]),
            motif: "light",
            dataTestId: "support-btn",
            openInNewTab: true,
            children: formatMessage("commons.store")
          }), /* @__PURE__ */jsx(ActionIcon, {
            "aria-hidden": "true",
            $motif: theme,
            onClick: e => handleModalOpen(e, ModalId.Language),
            children: /* @__PURE__ */jsx(StyleableSvgImage, {
              src: globe__default,
              width: 20,
              className: "header-icon",
              height: 19,
              renderedHeight: 18
            })
          }), account?.loading ? /* @__PURE__ */jsx(SkeletonCircleLoader, {
            diameter: 18
          }) : /* @__PURE__ */jsx(ActionIcon, {
            "aria-hidden": "true",
            $motif: theme,
            onClick: e => handleModalOpen(e, ModalId.Account),
            children: /* @__PURE__ */jsx(StyleableSvgImage, {
              src: accountLogo__default,
              width: 18,
              height: 19,
              className: "header-icon"
            })
          })]
        })]
      }), /* @__PURE__ */jsx(ContentContainer, {
        $isCollapsed: activeModal !== ModalId.MobileMenu,
        children: /* @__PURE__ */jsx(Content, {
          children: /* @__PURE__ */jsx(ModalMenuWrap, {
            size: "mobile",
            $activeModal: !!activeModal,
            children: /* @__PURE__ */jsx(ModalMenuInnerWrap, {
              $modal: ModalId.MobileMenu,
              children: /* @__PURE__ */jsx(OverflowWrapper, {
                children: /* @__PURE__ */jsx(MicrositeMobileMenu, {
                  menu,
                  isOpen: activeModal === ModalId.MobileMenu,
                  onMenuItemClick,
                  onModalClose: handleModalClose
                })
              })
            })
          })
        })
      }), activeModal === ModalId.Language && (isMobile ? /* @__PURE__ */jsx(MobileModal, {
        isOpen: activeModal === ModalId.Language,
        handleClose: handleModalClose,
        lockScroll: true,
        closeButtonProps: {
          value: formatMessage("commons.close"),
          variant: "cancel"
        },
        children: /* @__PURE__ */jsx(LanguageFrameMobile, {
          onLanguageChange,
          onClose: handleModalClose
        })
      }) : /* @__PURE__ */jsx(ModalWrap, {
        size: "desktop",
        children: /* @__PURE__ */jsx(ModalInnerWrap, {
          $modal: ModalId.Language,
          children: /* @__PURE__ */jsx(LanguageFrame, {
            open: true,
            onClose: handleModalClose,
            onLanguageChange
          })
        })
      })), activeModal === ModalId.Account && (isMobile ? /* @__PURE__ */jsx(MobileModal, {
        isOpen: activeModal === ModalId.Account,
        handleClose: handleModalClose,
        lockScroll: true,
        closeButtonProps: {
          value: formatMessage("commons.close"),
          variant: "cancel"
        },
        children: /* @__PURE__ */jsx(AccountFrameMobile, {
          onClose: handleModalClose,
          account
        })
      }) : /* @__PURE__ */jsx(ModalWrap, {
        size: "desktop",
        children: /* @__PURE__ */jsx(ModalInnerWrap, {
          $modal: ModalId.Account,
          children: /* @__PURE__ */jsx(AccountFrame, {
            open: true,
            onClose: handleModalClose,
            account
          })
        })
      }))]
    })]
  });
};
export { HeaderContent };
//# sourceMappingURL=HeaderContent.mjs.map
